import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.clients_api.clients;


const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'clients_api',
  group: 'clients',
  defaultTtl: '2m',
  hashLargeSegments: true,
  emitMeta: true,
});

// /GetClientById/<int:client_id>

export const getClientById = async (clientId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetClientById/${clientId}`)
  );
}

// Function to login a client
export const loginClient = async (email: string | null,
     password: string | null,
      token: string | null,
      provider: string | null) => {
  return await retryWithBackoff(() =>
    api.post('/LoginClient', {
      email: email,
      password: password,
      token: token,
      provider: provider
    })
  );
};

// Function to create a new client
export const createClient = async (
  username: string | null,
  password: string | null,
  token: string | null,
  provider: string | null,
  lastName: string | null,
  dob: string | null,
  address: string | null,
  email: string | null,
  phoneNumber: string | null,
  middleName: string | null
) => {
  const res = await retryWithBackoff(() =>
    api.post('/CreateClient', {
      username: username,
      password: password,
      token: token,
      provider: provider,
      last_name: lastName,
      dob: dob,
      address: address,
      email: email,
      phone_number: phoneNumber,
      middle_name: middleName
    })
  );
  await cachedApi.clear();
  return res;
};

// /GetClientInfoByEmail/<string:email>

export const getClientInfoByEmail = async (email: string) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetClientInfoByEmail/${email}`)
  );
}

/**
 * @ns.route('/UpdateClientDobByClientId/<int:client_id>')
@ns.param('client_id', 'The client identifier')
class UpdateClientDobByClientId(Resource):
    @ns.doc('update_client_dob_by_client_id')
    @ns.expect(update_dob_parser)
    @jwt_required()
    def put(self, client_id):
        """
        Update a client's date of birth by ID.
        
        Tracks all DOB changes in client_metadata.dob_updates (max 2 updates allowed).
        If the client has already updated their DOB twice, the update will be rejected.
        """
        from datetime import datetime
        
        # Get the client
        client = Client.query.get(client_id)
        if not client:
            return {'message': f'Client with ID {client_id} not found'}, 404

        args = update_dob_parser.parse_args()
        new_dob = args['dob']
        
        # Validate date format
        try:
            datetime.strptime(new_dob, '%Y-%m-%d')
        except ValueError:
            return {'message': 'Invalid date format. Use YYYY-MM-DD'}, 400
        
        # Initialize metadata if it doesn't exist
        if client.client_metadata is None:
            client.client_metadata = {}
        
        # Initialize dob_updates list if it doesn't exist
        if 'dob_updates' not in client.client_metadata:
            client.client_metadata['dob_updates'] = []
        
        # Check if the client has already updated their DOB twice
        dob_updates = client.client_metadata.get('dob_updates', [])
        if len(dob_updates) >= 2:
            return {
                'message': 'Maximum number of date of birth updates reached. Clients can only update their DOB twice.',
                'current_dob': str(client.dob) if client.dob else None,
                'update_history': dob_updates
            }, 403
        
        # Record the old DOB before updating
        old_dob = str(client.dob) if client.dob else None
        
        # Update the DOB
        client.dob = new_dob
        
        # Add update record to metadata
        update_record = {
            'previous_dob': old_dob,
            'new_dob': new_dob,
            'updated_at': datetime.utcnow().isoformat()
        }
        client.client_metadata['dob_updates'].append(update_record)
        
        # Mark the metadata field as modified for SQLAlchemy to detect the change
        from sqlalchemy.orm.attributes import flag_modified
        flag_modified(client, 'client_metadata')
        
        db.session.commit()
        
        return {
            'message': 'Date of birth updated successfully',
            'client_id': client.client_id,
            'dob': str(client.dob),
            'updates_remaining': 2 - len(client.client_metadata['dob_updates']),
            'update_history': client.client_metadata['dob_updates']
        }, 200
 */

export const updateClientDobByClientId = async (clientId: number, newDob: string) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateClientDobByClientId/${clientId}`, {
      dob: newDob
    })
  );
  await cachedApi.clear();
  return res;
}
  